import { defaultStoreUtils } from '@databases/redux';
import {
  getDatabaseWithCurrentView,
  selectHasSelectedCells,
  selectSelectedValues,
} from '@databases/redux/cellSelections/selectors';
import { container } from '@databases/service-container';
import { DatabaseFieldType } from '@databases/types/database';
import { isEmptyValue } from '@databases/utils/database/field-types';
import {
  getDatabaseEntryRequired,
  getDatabaseFieldRequired,
  getDatabaseValue,
} from '@databases/utils/database/utils';
// FIXME circular dependency
// eslint-disable-next-line workspaces/require-dependency
import useView from '@databases/views/useView';
import { useCallback, useEffect } from 'react';

import { copyHtmlTableToClipboard } from '../helpers/copy-helper';
import { isEmptyClipboard } from '../helpers/is-empty-clipboard';

import { useCopyWithDsp } from './useCopyWithDsp';

interface UseCopyDatabaseContentProps {
  contentId: string;
}

type RootState = ReturnType<
  ReturnType<(typeof defaultStoreUtils)['useStore']>['getState']
>;

type CopyHandler = (event: ClipboardEvent) => void;

const shouldDatabasesHandleCopy = (state: RootState, contentId: string) => {
  const selectionType = getSelection()?.type;
  const hasSelection = selectHasSelectedCells(state, { contentId });
  return selectionType !== 'Range' && hasSelection;
};

/**
 * Registers a document event listener that reacts to copy events.
 * This is used to copy the selected database content to the clipboard.
 */
export const useCopyListener = ({ contentId }: UseCopyDatabaseContentProps) => {
  const { view } = useView();
  const store = defaultStoreUtils.useStore();
  const { attemptCopy } = useCopyWithDsp();

  const areNonEmptyAttachmentsSelected = useCallback(() => {
    if (contentId) {
      const state = store.getState();
      const database = getDatabaseWithCurrentView(state, {
        contentId,
      });

      const selectedValues = selectSelectedValues(state, { contentId });
      const selectedAttachmentValues = selectedValues.filter(
        (value) =>
          getDatabaseFieldRequired(database, value.fieldId).type ===
          DatabaseFieldType.ATTACHMENT
      );

      return selectedAttachmentValues.some((selectedValue) => {
        const field = getDatabaseFieldRequired(database, selectedValue.fieldId);
        const entry = getDatabaseEntryRequired(database, selectedValue.entryId);
        const value = getDatabaseValue(database, field, entry);
        return !isEmptyValue({ database, field, entry, value }, state);
      });
    }
    return false;
  }, [contentId, store]);

  const handleExportDatabaseContent = useCallback(
    (state: ReturnType<(typeof store)['getState']>) => {
      copyHtmlTableToClipboard(contentId, state, view.settings);
    },
    [contentId, view.settings]
  );

  /**
   * @returns whether the callback handled the copy event or not
   */
  const handleCopyInsideDatabase = useCallback(
    (e: ClipboardEvent) => {
      const state = store.getState();

      if (shouldDatabasesHandleCopy(state, contentId)) {
        e.preventDefault();
        handleExportDatabaseContent(state);
        return true;
      } else {
        return false;
      }
    },
    [handleExportDatabaseContent, store, contentId]
  );

  const copyListener: CopyHandler = useCallback(
    (e) => {
      const hasNonEmptyAttachments = areNonEmptyAttachmentsSelected();

      const handledByDatabasesOrDsp = attemptCopy(() => {
        return handleCopyInsideDatabase(e);
      }, hasNonEmptyAttachments);

      if (handledByDatabasesOrDsp) {
        return;
      }

      if (!isEmptyClipboard(e)) {
        // Still gotta make sure that if there's any data in the payload, we let
        // the browser decide what to do - We only want to handle empty payloads
        return;
      }

      e.preventDefault();
      // Forward to Whiteboards when databases doesn't handle it (empty payload case)
      const eventBus = container.resolve('EventBus');
      eventBus.dispatch('forwardCopyClipboardToParent', null);
    },
    [attemptCopy, handleCopyInsideDatabase, areNonEmptyAttachmentsSelected]
  );

  useEffect(() => {
    document.addEventListener('copy', copyListener);

    return () => {
      document.removeEventListener('copy', copyListener);
    };
  }, [copyListener]);
};
